import React, { useEffect, useState } from 'react';
import { Video, MessageCircle, X } from 'lucide-react';
import { socket } from '../../services/socket';
import { debugLog } from '../../utils/logger';

/**
 * Componente de notificación de presencia en tiempo real
 * Muestra un toast cuando el doctor/paciente entra a chat o video
 */
function PresenceNotification({ appointmentId, currentUserRole }) {
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (!appointmentId) return;

    debugLog('\n👁️ [PresenceNotification] Registrando listeners de presencia');
    debugLog('📊 Appointment ID:', appointmentId);
    debugLog('👤 Current Role:', currentUserRole);

    // Listener para cuando alguien entra a video
    const handleVideoPresence = (data) => {
      debugLog('\n🎥 [handleVideoPresence] Usuario entró a video:', data);
      
      // No mostrar notificación de uno mismo
      if (data.userRole === currentUserRole) {
        debugLog('⚠️ Ignorando presencia propia');
        return;
      }

      setNotification({
        type: 'video',
        userName: data.userName,
        userRole: data.userRole,
        timestamp: Date.now()
      });

      // Auto-ocultar después de 5 segundos
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    };

    // Listener para cuando alguien entra a chat
    const handleChatPresence = (data) => {
      debugLog('\n💬 [handleChatPresence] Usuario entró a chat:', data);
      
      // No mostrar notificación de uno mismo
      if (data.userRole === currentUserRole) {
        debugLog('⚠️ Ignorando presencia propia');
        return;
      }

      setNotification({
        type: 'chat',
        userName: data.userName,
        userRole: data.userRole,
        timestamp: Date.now()
      });

      // Auto-ocultar después de 5 segundos
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    };

    socket.on('presence:video:joined', handleVideoPresence);
    socket.on('presence:chat:joined', handleChatPresence);

    return () => {
      debugLog('🧹 [PresenceNotification] Limpiando listeners');
      socket.off('presence:video:joined', handleVideoPresence);
      socket.off('presence:chat:joined', handleChatPresence);
    };
  }, [appointmentId, currentUserRole]);

  if (!notification) return null;

  const isVideo = notification.type === 'video';
  const roleText = notification.userRole === 'doctor' ? 'El doctor' : 'El paciente';

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div
        className={`${
          isVideo ? 'bg-blue-600' : 'bg-green-600'
        } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 max-w-md`}
      >
        <div className="flex-shrink-0">
          {isVideo ? (
            <Video className="h-6 w-6" />
          ) : (
            <MessageCircle className="h-6 w-6" />
          )}
        </div>
        
        <div className="flex-1">
          <p className="font-semibold text-sm">
            {roleText} {notification.userName} se unió
          </p>
          <p className="text-xs opacity-90">
            {isVideo ? 'Videollamada' : 'Chat'} - {appointmentId.substring(0, 8)}
          </p>
        </div>

        <button
          onClick={() => setNotification(null)}
          className="flex-shrink-0 hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default PresenceNotification;
